// S3FileUpload.tsx
import { baseUrl, resolutionHeader } from '../../Tenants';

class S3FileUpload {
  /**
   * Uploads a file to S3 and returns file metadata
   * @param {File} file - The file to upload
   * @param {Object} config - Configuration object containing bucketName, dirName (optional), url
   * @param {string} newFileName - The name to give the file in S3
   * @returns {Promise<Object>} - Object containing bucket, key, location, and result
   */
  static async uploadFile(
    file: any,
    config: any,
    newFileName: any
  ): Promise<any> {
    try {
      const authToken = localStorage.getItem('auth');

      if (!authToken) {
        throw new Error('Authentication token not found');
      }

      // Regex to identify non-ASCII characters that could cause issues with S3.
      const nonAsciiCharsRegex = /[^\x00-\x7F]/g;

      const sanitizedFileName = newFileName.replace(nonAsciiCharsRegex, '');

      const formData = new FormData();
      formData.append('file', file);

      const objectKey = `${config.dirName ? config.dirName + '/' : ''}${sanitizedFileName}`;

      if (!objectKey || objectKey.trim() === '') {
        throw new Error('Object key cannot be empty');
      }

      const headers: any = {
        Authorization: `Bearer ${JSON.parse(authToken)}`,
        key: objectKey,
        'Tenant-Id': resolutionHeader,
      };

      const response = await fetch(
        `${baseUrl}api/common/upload-object?api-version=2.0`,
        {
          method: 'POST',
          headers: headers,
          body: formData,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `S3 upload failed with status ${response.status}: ${errorText}`
        );
      }

      const data = await response.json();

      return {
        bucket: config.bucketName,
        key: objectKey,
        location: data?.url ?? config.url + objectKey,
        result: response,
      };
    } catch (error) {
      console.error('Error uploading file to S3:', error);
      throw error; // Re-throw to allow calling code to handle the error.
    }
  }

  /**
   * Deletes a file from S3 by its URL
   * @param {string} fileUrl - The full S3 URL of the file to delete
   * @returns {Promise<void>}
   */
  static async deleteFile(fileUrl: any): Promise<void> {
    try {
      const authToken = localStorage.getItem('auth');

      if (!authToken) {
        throw new Error('Authentication token not found');
      }

      const url = new URL(fileUrl);
      const objectKey = url.pathname.substring(1); // Remove leading slash.

      const headers: any = {
        Authorization: `Bearer ${JSON.parse(authToken)}`,
        'Tenant-Id': resolutionHeader,
      };

      const encodedUriComponent: string = encodeURIComponent(objectKey);

      const response = await fetch(
        `${baseUrl}api/Common/attachments/${encodedUriComponent}`,
        {
          method: 'DELETE',
          headers: headers,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `S3 delete failed with status ${response.status}: ${errorText}`
        );
      }
    } catch (error) {
      console.error('Error deleting file from S3:', error);
      throw error;
    }
  }
}

const { uploadFile, deleteFile } = S3FileUpload;
export { uploadFile, deleteFile };
export default S3FileUpload;
